import axios from 'axios';
import { storage } from './storage';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API_BASE_URL = `${BACKEND_URL}/api`;

// Flag para evitar múltiplas tentativas de refresh simultâneas
let isRefreshing = false;
let refreshSubscribers = [];

// Adiciona callbacks para serem executados após o refresh
const subscribeTokenRefresh = (callback) => {
  refreshSubscribers.push(callback);
};

// Executa todos os callbacks após o refresh
const onTokenRefreshed = (newToken) => {
  refreshSubscribers.forEach(callback => callback(newToken));
  refreshSubscribers = [];
};

// Rejeita todos os callbacks se o refresh falhar
const onRefreshError = (error) => {
  refreshSubscribers.forEach(callback => callback(null, error));
  refreshSubscribers = [];
};

/**
 * Format API error messages for display
 * Handles various FastAPI error formats:
 * - string: "Error message"
 * - array: [{loc: ["body", "field"], msg: "message", type: "error_type"}]
 * - object: {detail: "message"} or {detail: [...]}
 */
export function formatApiError(error) {
  if (!error) return 'Ocorreu um erro desconhecido';
  
  // If it's an axios error, extract the response
  const data = error.response?.data || error;
  
  // Handle string directly
  if (typeof data === 'string') return data;
  
  // Handle detail field
  const detail = data.detail || data.message || data.error;
  
  if (!detail) {
    // If no detail, try to get status text
    if (error.response?.statusText) return error.response.statusText;
    if (error.message) return error.message;
    return 'Ocorreu um erro desconhecido';
  }
  
  // Handle string detail
  if (typeof detail === 'string') return detail;
  
  // Handle array of validation errors (FastAPI format)
  if (Array.isArray(detail)) {
    const messages = detail.map(err => {
      if (typeof err === 'string') return err;
      // FastAPI validation error format: {loc: ["body", "field"], msg: "message"}
      const field = err.loc ? err.loc.slice(1).join('.') : '';
      const msg = err.msg || err.message || 'Valor inválido';
      return field ? `${field}: ${msg}` : msg;
    });
    return messages.join('; ');
  }
  
  // Handle object with message
  if (typeof detail === 'object') {
    return detail.message || detail.msg || JSON.stringify(detail);
  }
  
  return String(detail);
}

// Create axios instance with defaults
const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000,
});

// Request interceptor to add auth token
api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('magicstay_token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error)
);

// Response interceptor - handles 401 Unauthorized with token refresh
api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;
    
    // Handle 401 Unauthorized - session expired or invalid token
    if (error.response?.status === 401) {
      const url = originalRequest?.url || '';
      
      // Don't try to refresh for auth endpoints
      if (url.includes('/auth/login') || url.includes('/auth/register') || 
          url.includes('/auth/admin') || url.includes('/auth/refresh')) {
        return Promise.reject(error);
      }
      
      // Don't retry if already tried
      if (originalRequest._retry) {
        // Refresh failed, clear session and redirect
        handleAuthFailure();
        return new Promise(() => {});
      }
      
      // Mark as retry attempt
      originalRequest._retry = true;
      
      // If already refreshing, wait for the refresh to complete
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          subscribeTokenRefresh((newToken, error) => {
            if (error) {
              reject(error);
            } else if (newToken) {
              originalRequest.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(originalRequest));
            } else {
              reject(new Error('Token refresh failed'));
            }
          });
        });
      }
      
      // Try to refresh the token
      isRefreshing = true;
      const currentToken = localStorage.getItem('magicstay_token');
      
      if (!currentToken) {
        isRefreshing = false;
        handleAuthFailure();
        return new Promise(() => {});
      }
      
      try {
        console.log('[API] Attempting token refresh...');
        const response = await axios.post(`${API_BASE_URL}/auth/refresh`, {}, {
          headers: { Authorization: `Bearer ${currentToken}` }
        });
        
        const { token: newToken, user } = response.data;
        
        // Save new token and user data
        localStorage.setItem('magicstay_token', newToken);
        localStorage.setItem('magicstay_user', JSON.stringify(user));
        
        console.log('[API] Token refreshed successfully');
        
        // Update authorization header for original request
        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        
        // Notify all waiting requests
        onTokenRefreshed(newToken);
        isRefreshing = false;
        
        // Retry the original request
        return api(originalRequest);
      } catch (refreshError) {
        console.log('[API] Token refresh failed:', refreshError.response?.data || refreshError.message);
        onRefreshError(refreshError);
        isRefreshing = false;
        
        // Refresh failed, clear session and redirect
        handleAuthFailure();
        return new Promise(() => {});
      }
    }
    
    return Promise.reject(error);
  }
);

// Handle authentication failure - clear session and redirect
function handleAuthFailure() {
  const currentPath = window.location.pathname;
  const currentSearch = window.location.search;
  
  // Don't redirect if already on auth pages
  if (['/login', '/register', '/forgot-password', '/reset-password'].includes(currentPath)) {
    return;
  }
  
  console.log('[API] Authentication failed - Clearing session and redirecting to login');
  
  // Clear all auth data using storage utility
  storage.clearAuth();
  
  // Build redirect URL with next parameter and expired flag
  const fullPath = currentPath + currentSearch;
  const encodedNext = encodeURIComponent(fullPath);
  
  // Force dispatch authChanged event to trigger RequireAuth re-check
  window.dispatchEvent(new CustomEvent('authChanged', { detail: { reason: '401' } }));
  
  // Redirect to login with the current path as next parameter
  // Add expired=true to indicate session actually expired (vs. just not logged in)
  const redirectUrl = `/login?next=${encodedNext}&expired=true`;
  console.log('[API] Redirecting to:', redirectUrl);
  
  // Use a small delay to ensure state is cleared before redirect
  setTimeout(() => {
    window.location.replace(redirectUrl);
  }, 50);
}

// Named export for convenience
export { api };
export default api;

// Auth API
export const authAPI = {
  register: (data) => api.post('/auth/register', data),
  login: (data) => api.post('/auth/login', data),
  forgotPassword: (data) => api.post('/auth/forgot-password', data),
  resetPassword: (data) => api.post('/auth/reset-password', data),
};

// Plans API
export const plansAPI = {
  getAll: () => api.get('/subscription-plans'),
};

// Trial API
export const trialAPI = {
  checkEligibility: (data) => api.post('/trial/check-eligibility', data),
};

// Subscriptions API
export const subscriptionsAPI = {
  createCheckout: (data) => api.post('/subscriptions/create-checkout', data),
  getCheckoutStatus: (sessionId) => api.get(`/subscriptions/checkout-status/${sessionId}`),
  getStatus: () => api.get('/user/subscription-status'),
  cancel: () => api.post('/user/cancel-subscription'),
};

// Properties API
export const propertiesAPI = {
  getAll: (params) => api.get('/properties', { params }),
  getById: (id) => api.get(`/properties/${id}`),
  create: (data) => api.post('/properties', data),
  update: (id, data) => api.put(`/properties/${id}`, data),
  delete: (id) => api.delete(`/properties/${id}`),
};

// Contact API
export const contactAPI = {
  createLead: (data) => api.post('/contact-leads', data),
};

// Reviews API
export const reviewsAPI = {
  create: (data) => api.post('/reviews', data),
  getByProperty: (propertyId) => api.get(`/properties/${propertyId}/reviews`),
  createReply: (reviewId, data) => api.post(`/reviews/${reviewId}/reply`, data),
};
